import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Sun } from "lucide-react";
import { IMAGES, COMPANY } from "../../lib/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const sunY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const lines = ["Power your", "tomorrow,", "today"];

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden bg-cream pt-28">
      {/* blueprint grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: "linear-gradient(#0F3D2A 1px,transparent 1px),linear-gradient(90deg,#0F3D2A 1px,transparent 1px)", backgroundSize: "64px 64px" }} />

      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-6 pb-20 md:px-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
        <div className="relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-white/60 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-forest backdrop-blur">
            <Sun className="h-4 w-4 text-gold" /> {COMPANY.region} · Rooftop Solar
          </motion.p>

          <h1 className="font-heading text-5xl font-extrabold leading-[0.92] tracking-tight text-forest sm:text-6xl lg:text-[5.2rem]">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {l}{i === lines.length - 1 && <span className="text-gold">.</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="mt-7 max-w-md text-base leading-relaxed text-moss md:text-lg">
            Synergy Solar designs, installs and maintains premium rooftop solar for homes and businesses across Chandigarh, Punjab, Haryana & New Delhi — with Tier-1 panels, full subsidy support and a 25-year warranty.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#quote" data-testid="hero-cta"
              className="rounded-full bg-forest px-8 py-4 font-semibold text-cream transition-transform duration-300 hover:scale-95 hover:bg-forest-hover">
              Get a free quote
            </a>
            <a href="#savings" className="rounded-full border border-line px-8 py-4 font-semibold text-forest transition-colors hover:bg-sand">
              Calculate savings
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            className="mt-10 flex items-center gap-6 text-sm text-moss">
            <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-forest" /> 2,000+ installs</span>
            <span className="font-mono">4.9★ · 25-yr warranty</span>
          </motion.div>
        </div>

        {/* image + floating sun */}
        <div className="relative h-[420px] sm:h-[520px] lg:h-[640px]">
          <motion.div style={{ y: sunY }} className="absolute -right-6 top-4 z-20 hidden lg:block">
            <div className="animate-float">
              <div className="relative flex h-40 w-40 items-center justify-center rounded-full bg-gold shadow-[0_20px_60px_rgba(230,156,36,0.4)]">
                <div className="absolute inset-0 animate-spinslow rounded-full border-2 border-dashed border-obsidian/20" />
                <span className="font-mono text-sm font-semibold text-obsidian">300+ sunny<br/>days / yr</span>
              </div>
            </div>
          </motion.div>

          <motion.div style={{ y: imgY }} initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 overflow-hidden rounded-[2rem]">
            <img src={IMAGES.heroBg} alt="Rooftop solar in an Indian neighbourhood" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-forest/10 mix-blend-multiply" />
          </motion.div>
        </div>
      </div>

      <a href="#how" className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 text-xs uppercase tracking-widest text-moss lg:flex">
        <ArrowDown className="h-4 w-4 animate-bounce" /> Scroll
      </a>
    </section>
  );
}
